import React from 'react';
import { Tag } from 'antd';
import { palette } from '@shipium-corp/shipium-ui-theme';
import type { PaletteColor } from '@shipium-corp/shipium-ui-theme';
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
  SyncOutlined,
  DeleteOutlined,
  StopOutlined,
  HourglassOutlined,
} from '@ant-design/icons';
import type { ShiptimeCarrierLabel } from '@shipium-corp/shipium-ts-types';

export const IconMap = {
  check: <CheckCircleOutlined style={{ marginLeft: 5, marginTop: 3, fontSize: '14px' }} />,
  warning: <WarningOutlined style={{ marginLeft: 5, marginTop: 3, fontSize: '14px' }} />,
  exclamation: <ExclamationCircleOutlined style={{ marginLeft: 5, marginTop: 3, fontSize: '14px' }} />,
  question: <QuestionCircleOutlined style={{ marginLeft: 5, marginTop: 3, fontSize: '14px' }} />,
  config: <SettingOutlined style={{ marginLeft: 5, marginTop: 3, fontSize: '14px' }} />,
  processing: <SyncOutlined spin style={{ marginLeft: 5, marginTop: 3, fontSize: '14px' }} />,
  delete: <DeleteOutlined style={{ marginLeft: 5, marginTop: 3, fontSize: '14px' }} />,
  stop: <StopOutlined style={{ marginLeft: 5, marginTop: 3, fontSize: '14px' }} />,
  wait: <HourglassOutlined style={{ marginLeft: 5, marginTop: 3, fontSize: '14px' }} />,
};

export interface ShipiumTagProps {
  color?: PaletteColor;
  children: string | number | React.ReactNode;
  style?: React.CSSProperties;
  dark?: boolean;
  closable?: boolean;
  onClose?: any;
  icon?: React.ReactNode;
  wrap?: boolean;
}

export interface StatusTagProps {
  style?: React.CSSProperties;
  dark?: boolean;
  closable?: boolean;
  onClose?: any;
  status: ShiptimeCarrierLabel['status'];
  children?: React.ReactNode;
}
export interface TrackingStatusTagProps {
  style?: React.CSSProperties;
  dark?: boolean;
  closable?: boolean;
  onClose?: any;
  status: string;
  children?: React.ReactNode;
}

export interface ShipiumTagWithConfigProps {
  configMap: Record<string, ShipiumTagConfig>;
  tag: string;
  style?: React.CSSProperties;
}

export interface ShipiumTagConfig {
  color: PaletteColor;
  label: string;
  dark?: boolean;
  icon?: 'check' | 'warning' | 'exclamation' | 'question' | 'config' | 'processing' | 'delete' | 'stop' | 'wait';
}

export const ShipiumTag = ({
  color = 'Teal',
  children,
  style,
  dark = false,
  closable = false,
  onClose,
  icon,
  wrap = false,
}: ShipiumTagProps) => (
  <Tag
    icon={icon}
    closable={closable}
    onClose={onClose}
    style={style}
    color={dark ? palette[color].light : palette[color].lightest}
  >
    <span
      style={{
        whiteSpace: wrap ? 'normal' : 'nowrap',
        fontWeight: dark ? 'bolder' : 'inherit',
        color: dark ? '#fff' : palette[color].darkest,
      }}
    >
      {children}
    </span>
  </Tag>
);

export const ShipiumTagWithConfig = ({ configMap, tag, style }: ShipiumTagWithConfigProps) => {
  const config = configMap[tag];
  const icon = config?.icon ? IconMap[config.icon] : undefined;

  return config ? (
    <ShipiumTag style={style} dark={config.dark} color={config.color}>
      {config.label}
      {icon}
    </ShipiumTag>
  ) : (
    <div>ERROR: UNMAPPED TAG</div>
  );
};

export const BooleanShipiumTag = ({ value }: { value: boolean }) => {
  return (
    <ShipiumTag dark={true} color={value === true ? 'Teal' : 'Coral'}>
      {value === true ? 'True' : 'False'}
    </ShipiumTag>
  );
};

// failover, failure, carrier_selection_failure, generated, label_created, manifest_failure, manifested, success, voided
export const LabelStatusTag = ({
  status,
  children,
  style,
  dark = false,
  closable = false,
  onClose,
}: StatusTagProps) => {
  const spacedStyle = { ...style, marginBottom: '5px' };

  switch (status) {
    case 'failover':
      return (
        <span>
          <ShipiumTag color={'Teal'} onClose={onClose} closable={closable} style={spacedStyle} dark={dark}>
            Success
          </ShipiumTag>
          <ShipiumTag color={'Violet'} onClose={onClose} closable={closable} style={style} dark={dark}>
            Failover
          </ShipiumTag>
        </span>
      );
    case 'failure':
      return (
        <ShipiumTag color={'Coral'} onClose={onClose} closable={closable} style={style} dark={dark}>
          Failure
        </ShipiumTag>
      );
    case 'carrier_selection_failure':
      return (
        <ShipiumTag color={'Coral'} onClose={onClose} closable={closable} style={style} dark={dark}>
          Carrier Selection Failure
        </ShipiumTag>
      );
    case 'generated':
      return (
        <span>
          <ShipiumTag color={'Teal'} onClose={onClose} closable={closable} style={spacedStyle} dark={dark}>
            Success
          </ShipiumTag>
          <ShipiumTag color={'Blue'} onClose={onClose} closable={closable} style={style} dark={dark}>
            Generated
          </ShipiumTag>
        </span>
      );
    case 'label_created':
      return (
        <span>
          <ShipiumTag color={'Teal'} onClose={onClose} closable={closable} style={spacedStyle} dark={dark}>
            Success
          </ShipiumTag>
          <ShipiumTag color={'Blue'} onClose={onClose} closable={closable} style={style} dark={dark}>
            Created
          </ShipiumTag>
        </span>
      );
    case 'manifest_failure':
      return (
        <span>
          <ShipiumTag color={'Teal'} onClose={onClose} closable={closable} style={spacedStyle} dark={dark}>
            Success
          </ShipiumTag>
          <ShipiumTag color={'Coral'} onClose={onClose} closable={closable} style={style} dark={dark}>
            Manifest Failure
          </ShipiumTag>
        </span>
      );
    case 'manifested':
      return (
        <span>
          <ShipiumTag color={'Teal'} onClose={onClose} closable={closable} style={spacedStyle} dark={dark}>
            Success
          </ShipiumTag>
          <ShipiumTag color={'Blue'} onClose={onClose} closable={closable} style={style} dark={dark}>
            Manifested
          </ShipiumTag>
        </span>
      );
    case 'success':
      return (
        <ShipiumTag color={'Teal'} onClose={onClose} closable={closable} style={style} dark={dark}>
          Success
        </ShipiumTag>
      );
    case 'voided':
      return (
        <span>
          <ShipiumTag color={'Teal'} onClose={onClose} closable={closable} style={spacedStyle} dark={dark}>
            Success
          </ShipiumTag>
          <ShipiumTag color={'Fuschia'} onClose={onClose} closable={closable} style={style} dark={dark}>
            Voided
          </ShipiumTag>
        </span>
      );
    default:
      return (
        <ShipiumTag color={'Gold'} onClose={onClose} closable={closable} style={style} dark={dark}>
          N/A
        </ShipiumTag>
      );
  }
};

export const TrackingStatusTag = ({
  status,
  children,
  style,
  dark = false,
  closable = false,
  onClose,
}: TrackingStatusTagProps) => {
  switch (status) {
    case 'InTransit':
    case 'In Transit':
      return (
        <ShipiumTag color={'Blue'} onClose={onClose} closable={closable} style={style} dark={dark}>
          In Transit
        </ShipiumTag>
      );
    case 'Unknown':
      return (
        <ShipiumTag color={'Violet'} onClose={onClose} closable={closable} style={style} dark={dark}>
          Unknown
        </ShipiumTag>
      );
    case 'OutForDelivery':
    case 'Out For Delivery':
      return (
        <ShipiumTag color={'Blue'} onClose={onClose} closable={closable} style={style} dark={dark}>
          Out For Delivery
        </ShipiumTag>
      );
    case 'Exception':
      return (
        <ShipiumTag color={'Coral'} onClose={onClose} closable={closable} style={style} dark={dark}>
          Exception
        </ShipiumTag>
      );
    case 'ArrivedCarrierFacility':
      return (
        <ShipiumTag color={'Blue'} onClose={onClose} closable={closable} style={style} dark={dark}>
          Arrived at Carrier Facility
        </ShipiumTag>
      );
    case 'Delivered':
      return (
        <ShipiumTag color={'Teal'} onClose={onClose} closable={closable} style={style} dark={dark}>
          Delivered
        </ShipiumTag>
      );
    case 'PickedUp':
      return (
        <ShipiumTag color={'Blue'} onClose={onClose} closable={closable} style={style} dark={dark}>
          Picked Up
        </ShipiumTag>
      );
    case 'Hold':
      return (
        <ShipiumTag color={'Fuschia'} onClose={onClose} closable={closable} style={style} dark={dark}>
          Hold
        </ShipiumTag>
      );
    case 'DepartedCarrierFacility':
      return (
        <ShipiumTag color={'Blue'} onClose={onClose} closable={closable} style={style} dark={dark}>
          Departed Carrier Facility
        </ShipiumTag>
      );
    case 'InfoReceived':
      return (
        <ShipiumTag color={'Blue'} onClose={onClose} closable={closable} style={style} dark={dark}>
          Info Received
        </ShipiumTag>
      );
    case 'Label Printed':
      return (
        <ShipiumTag color={'Blue'} onClose={onClose} closable={closable} style={style} dark={dark}>
          Label Printed
        </ShipiumTag>
      );
    case 'Registered':
      return (
        <ShipiumTag color={'Fuschia'} onClose={onClose} closable={closable} style={style} dark={dark}>
          Registered
        </ShipiumTag>
      );
    default:
      return (
        <ShipiumTag color={'Grey'} onClose={onClose} closable={closable} style={style} dark={dark}>
          N/A {status}
        </ShipiumTag>
      );
  }
};
